// Module: inventory — «المخزون» (HANDOFF §3 «/inventory/*», board 14 part 2
// §ز). Flag erp.inventory; manager+ by default (HANDOFF §9: inventory is
// hidden below manager) OR a staff member the owner gave the inventory.write
// switch — exactly what erp-core enforces on every /inventory/* and
// /suppliers route (auth.RequireCapability(inventory.write); Principal.Can is
// true for manager+). Expressed with the shell's OR gate (`anyOf`). Inside
// the screens, reversals and payables figures stay manager+ (the server
// omits them below manager; the screens remove the elements).
//
// Menu (board): المواد · المخازن والأرصدة · قوائم الشراء · الموردون · الجرد ·
// الحركات. «المخازن والأرصدة» has no artboard → absent (rule 13, question
// logged); warehouse balances show in each item's card.

import { Navigate, type RouteObject } from 'react-router-dom';
import { Package } from 'lucide-react';
import { defineModule, gate, type Gate, type MenuItem } from '@/app/module-types';
import { COPY } from './lib/copy';
import { INV } from './lib/links';

const FLAG = 'erp.inventory';
/** manager+ OR the inventory.write switch. Role first: its sentence is the one a refused deep link shows. */
export const INVENTORY_ACCESS: Gate[] = [{ minRole: 'manager' }, { capability: 'inventory.write' }];
const g = gate({ flag: FLAG, anyOf: INVENTORY_ACCESS });

const items = () => import('./screens/Items').then((m) => ({ Component: m.ItemsRoute }));
const purchases = () => import('./screens/Purchases').then((m) => ({ Component: m.PurchasesRoute }));
const purchase = () => import('./screens/PurchasePage').then((m) => ({ Component: m.PurchaseRoute }));
const suppliers = () => import('./screens/Suppliers').then((m) => ({ Component: m.SuppliersRoute }));
const counts = () => import('./screens/Counts').then((m) => ({ Component: m.CountsRoute }));
const count = () => import('./screens/CountPage').then((m) => ({ Component: m.CountRoute }));
const moves = () => import('./screens/Moves').then((m) => ({ Component: m.MovesRoute }));

export const inventoryRoutes: RouteObject[] = [
  { path: INV.root, element: <Navigate to={INV.items} replace />, handle: g },
  { path: INV.items, lazy: items, handle: g },
  { path: INV.newItem, lazy: items, handle: g },
  { path: '/inventory/items/:id', lazy: items, handle: g },
  { path: INV.purchases, lazy: purchases, handle: g },
  { path: INV.newPurchase, lazy: purchase, handle: g },
  { path: '/inventory/purchases/:id', lazy: purchase, handle: g },
  { path: INV.suppliers, lazy: suppliers, handle: g },
  { path: '/inventory/suppliers/new', lazy: suppliers, handle: g },
  { path: '/inventory/suppliers/:id', lazy: suppliers, handle: g },
  { path: INV.counts, lazy: counts, handle: g },
  { path: '/inventory/counts/:id', lazy: count, handle: g },
  { path: INV.moves, lazy: moves, handle: g },
];

const row = (key: string, label: string, to: string): MenuItem => ({ key, label, to, flag: FLAG, anyOf: INVENTORY_ACCESS });

export const inventoryMenu: MenuItem[] = [
  row('items', COPY.menu.items, INV.items),
  row('purchases', COPY.menu.purchases, INV.purchases),
  row('suppliers', COPY.menu.suppliers, INV.suppliers),
  row('counts', COPY.menu.counts, INV.counts),
  row('moves', COPY.menu.moves, INV.moves),
];

export default defineModule({
  key: 'inventory',
  label: COPY.module,
  icon: Package,
  flag: FLAG,
  minRole: 'staff',
  anyOf: INVENTORY_ACCESS,
  rail: 'inventory',
  routes: inventoryRoutes,
  menu: inventoryMenu,
});

export { INV } from './lib/links';
